import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { Plus, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_BASE_URL } from '../config/config';
import '../css/UserGroups.css';
import { useNavigate } from 'react-router-dom';
import OpsLayout from '../components/OpsLayout';
import { isSuperAdminSession } from '../components/opsNav';
import opsStatusTone from '../utils/opsStatusTone';
import UserGroupMembers from '../components/UserGroupMembers';

const GROUP_ROLES = [
    { value: 'MEMBER', label: '일반회원' },
    { value: 'CANDIDATE', label: '가입신청자' }
];

const getRoleLabel = (roleValue) => {
    const role = GROUP_ROLES.find(r => r.value === roleValue);
    return role ? role.label : roleValue;
};

const GroupModal = ({ group, onClose }) => {
    const navigate = useNavigate();
    const [editedGroup, setEditedGroup] = useState(group || {
        group_name: '',
        description: '',
        group_role: 'MEMBER',
        available: true
    });

    // 지역 및 병원 관련 상태 추가
    const [regions, setRegions] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState('');
    const [selectedDistrict, setSelectedDistrict] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedHospital, setSelectedHospital] = useState('');
    const [selectedHospitalInfo, setSelectedHospitalInfo] = useState(null);
    const [adminGroups, setAdminGroups] = useState([]);
    const [selectedAdminGroup, setSelectedAdminGroup] = useState('');
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [showHospitalSelector, setShowHospitalSelector] = useState(false);

    // 모달 안 탭. 새 그룹(id 없음)에는 구성원 탭이 없어 늘 'info' 다. 모달은 열 때마다
    // 새로 마운트되므로(부모가 selectedGroup·showCreateModal 이 없으면 렌더하지 않는다)
    // 이 상태는 열 때마다 자동으로 'info' 로 되돌아간다.
    const [modalTab, setModalTab] = useState('info');

    useEffect(() => {
        let isMounted = true;
        const initializeData = async () => {
            try {
                if (!isMounted) return;

                // 슈퍼 관리자 여부 확인
                const superAdminStatus = localStorage.getItem('is_super_admin') === 'true';
                setIsSuperAdmin(superAdminStatus);

                if (group) {
                    setShowHospitalSelector(false);
                    setEditedGroup({ ...group });
                    setSelectedRegion(group.region_name || '');
                    setSelectedDistrict(group.district_name || '');
                    setSelectedHospital(group.hospital_name || '');
                    setSearchTerm(group.hospital_name || '');
                    setSelectedHospitalInfo({
                        id: group.hospital_id,
                        institution_name: group.hospital_name,
                        type_name: group.hospital_type_name,
                        region_name: group.region_name,
                        district_name: group.district_name,
                        type_code: group.hospital_type_code,
                        region_code: group.region_code,
                        district_code: group.district_code,
                        postal_code: group.postal_code
                    });
                    await fetchAdminGroups(group);
                } else {
                    if (superAdminStatus) {
                        setShowHospitalSelector(true);
                        await fetchRegions();
                        await fetchAdminGroups(null); // 초기에는 전체 관리자 그룹 로드
                    } else {
                        const token = localStorage.getItem('token');
                        const adminGroupIds = JSON.parse(localStorage.getItem('admin_group_ids')) || [];
                        if (adminGroupIds.length > 0) {
                            const response = await axios.get(`${API_BASE_URL}/api/admin-groups`, {
                                headers: { Authorization: `Bearer ${token}` },
                                params: { admin_group_ids: adminGroupIds }
                            });

                            if (response.data.success && response.data.groups) {
                                const hospitalIds = new Set(response.data.groups.map(g => g.hospital_id));
                                if (hospitalIds.size > 1) {
                                    setShowHospitalSelector(true);
                                    await fetchRegions();
                                } else {
                                    setShowHospitalSelector(false);
                                    // 기존 단일 병원 로직
                                    const regionName = localStorage.getItem('region_name') || '';
                                    const districtName = localStorage.getItem('district_name') || '';
                                    const hospitalName = localStorage.getItem('hospital_name') || '';
                                    const hospitalId = localStorage.getItem('hospital_id') || '';
                                    const hospitalInfo = {
                                        id: hospitalId,
                                        institution_name: hospitalName,
                                        region_name: regionName,
                                        district_name: districtName
                                    };
                                    setSelectedRegion(regionName);
                                    setSelectedDistrict(districtName);
                                    setSelectedHospital(hospitalName);
                                    setSearchTerm(hospitalName);
                                    setSelectedHospitalInfo(hospitalInfo);
                                    await fetchAdminGroups({ hospital_id: hospitalId });
                                }
                            }
                        } else {
                            // 소속 그룹이 없는 경우, 단일 병원 UI로 기본 처리
                            setShowHospitalSelector(false);
                        }
                    }
                }
            } catch (error) {
                console.error('데이터 초기화 중 오류 발생:', error);
                alert('데이터를 불러오는 중 오류가 발생했습니다. 다시 시도해주세요.');
            }
        };
        initializeData();
        return () => { isMounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [group]);

    // 지역 목록 가져오기
    const fetchRegions = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/regions`);
            if (response.data.success) {
                setRegions(response.data.regions);
            }
        } catch (error) {
            console.error('지역 목록 가져오기 실패:', error);
            throw error;
        }
    };

    // 관리자 그룹 목록 가져오기
    const fetchAdminGroups = async (groupForHospital) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('인증 토큰이 없습니다.');

            const isSuperAdminNow = localStorage.getItem('is_super_admin') === 'true';
            let params = {};

            let hospitalIdToFetch = null;

            // 수정 모드일 때 (group 객체가 존재)
            if (groupForHospital && groupForHospital.hospital_id) {
                hospitalIdToFetch = groupForHospital.hospital_id;
            }
            // 생성 모드일 때
            else {
                if (isSuperAdminNow) {
                    // 슈퍼 관리자가 병원을 선택한 경우
                    if (selectedHospitalInfo && selectedHospitalInfo.id) {
                        hospitalIdToFetch = selectedHospitalInfo.id;
                    }
                } else {
                    // 일반 관리자는 자신의 병원 ID 사용
                    hospitalIdToFetch = localStorage.getItem('hospital_id');
                }
            }

            if (hospitalIdToFetch) {
                params.hospital_id = hospitalIdToFetch;
            } else if (isSuperAdminNow) {
                // 슈퍼관리자가 생성모드에서 아직 병원을 선택하지 않은 경우, 모든 관리자 그룹을 가져옴
                params.is_super_admin = 'true';
            } else {
                // 일반관리자가 병원 ID가 없는 예외적인 경우, 목록을 비움
                setAdminGroups([]);
                return;
            }

            const response = await axios.get(`${API_BASE_URL}/api/admin-groups`, {
                headers: { Authorization: `Bearer ${token}` },
                params: params
            });

            if (response.data.success) {
                setAdminGroups(response.data.groups || []);
                // 수정 모드에서만 기존 관리자 그룹을 선택
                if (groupForHospital && groupForHospital.admin_group_id) {
                    setSelectedAdminGroup(String(groupForHospital.admin_group_id));
                } else {
                    setSelectedAdminGroup('');
                }
            } else {
                throw new Error(response.data.message || '관리자 그룹 목록을 가져오는데 실패했습니다.');
            }
        } catch (error) {
            console.error('관리자 그룹 목록 가져오기 실패:', error);
            setAdminGroups([]); // 에러 발생 시 목록 비우기
        }
    };

    // 지역 선택 핸들러
    const handleRegionChange = async (e) => {
        const region = e.target.value;
        setSelectedRegion(region);
        setSelectedDistrict('');
        setSearchResults([]);
        setSearchTerm('');
        setSelectedHospital('');
        setSelectedHospitalInfo(null);
        setEditedGroup(prev => ({ ...prev, region: region, hospital: '' }));

        if (region) {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/districts/${encodeURIComponent(region)}`);
                if (response.data.success) {
                    setDistricts(response.data.districts);
                }
            } catch (error) {
                console.error('구/군 목록 가져오기 실패:', error);
                setDistricts([]);
            }
        } else {
            setDistricts([]);
        }
    };

    // 구/군 선택 핸들러
    const handleDistrictChange = (e) => {
        setSelectedDistrict(e.target.value);
        setSearchResults([]);
        setSearchTerm('');
        setSelectedHospital('');
        setSelectedHospitalInfo(null);
    };

    // 병원 검색 핸들러
    const handleHospitalSearch = async () => {
        if (!selectedRegion || !searchTerm.trim()) {
            alert('지역을 선택하고 병원명을 입력해주세요.');
            return;
        }

        try {
            const response = await axios.get(`${API_BASE_URL}/api/hospitals/search`, {
                params: {
                    region_name: selectedRegion,
                    district_name: selectedDistrict,
                    search_term: searchTerm
                }
            });

            if (response.data.success) {
                const hospitals = response.data.hospitals.map(hospital => ({
                    id: hospital.id,
                    institution_name: hospital.institution_name,
                    type_name: hospital.type_name,
                    region_name: hospital.region_name,
                    district_name: hospital.district_name,
                    address: hospital.address,
                    type_code: hospital.type_code,
                    region_code: hospital.region_code,
                    district_code: hospital.district_code,
                    postal_code: hospital.postal_code
                }));
                setSearchResults(hospitals);
            } else {
                setSearchResults([]);
                alert('병원 검색 결과가 없습니다.');
            }
        } catch (error) {
            console.error('병원 검색 실패:', error);
            setSearchResults([]);
            alert('병원 검색 중 오류가 발생했습니다.');
        }
    };

    // 병원 선택 핸들러
    const handleHospitalSelect = (hospital) => {
        setSelectedHospital(hospital.institution_name);
        setSearchTerm(hospital.institution_name);
        setSearchResults([]);
        setSelectedHospitalInfo(hospital);
        setEditedGroup(prev => ({
            ...prev,
            hospital: hospital.institution_name,
            region: hospital.region_name,
            district: hospital.district_name
        }));
        // 슈퍼 관리자가 병원을 선택하면 해당 병원의 관리자 그룹을 다시 불러옵니다.
        if (isSuperAdmin) {
            fetchAdminGroups({ hospital_id: hospital.id });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedGroup(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        if (!editedGroup.group_name.trim()) {
            alert('그룹명을 입력해주세요.');
            return;
        }

        if (!group && (!selectedRegion || !selectedHospital)) {
            alert('지역과 병원을 선택해주세요.');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                alert('로그인이 필요합니다.');
                navigate('/login');
                return;
            }

            let hospitalInfo = selectedHospitalInfo;
            // 새 그룹 생성 시 postal_code 또는 type_code가 없으면 병원 상세 조회
            if (!group && hospitalInfo && (!hospitalInfo.postal_code || !hospitalInfo.type_code)) {
                try {
                    const res = await axios.get(`${API_BASE_URL}/api/hospitals/info`, {
                        params: {
                            institution_name: hospitalInfo.institution_name,
                            region_name: hospitalInfo.region_name
                        }
                    });
                    if (res.data && res.data.success && res.data.hospital) {
                        hospitalInfo = res.data.hospital;
                    }
                } catch (err) {
                    alert('병원 상세 정보를 불러오지 못했습니다.');
                    return;
                }
            }

            const saveData = {
                group_name: editedGroup.group_name,
                description: editedGroup.description || '',
                group_role: editedGroup.group_role || 'MEMBER',
                available: Boolean(editedGroup.available),
                hospital_id: hospitalInfo?.id || group?.hospital_id,
                region_code: hospitalInfo?.region_code || group?.region_code,
                district_code: hospitalInfo?.district_code || group?.district_code,
                postal_code: hospitalInfo?.postal_code || group?.postal_code,
                hospital_type_code: hospitalInfo?.type_code || group?.hospital_type_code,
                admin_group_id: selectedAdminGroup || null
            };

            let response;
            if (group) {
                response = await axios.put(`/api/user-groups/${group.id}`, saveData, {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 30000
                });
            } else {
                response = await axios.post('/api/user-groups', saveData, {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 30000
                });
            }

            if (response.data.success) {
                alert(group ? '수정이 완료되었습니다.' : '생성이 완료되었습니다.');
                window.location.reload();
            } else {
                throw new Error(response.data.error || '저장 중 오류가 발생했습니다.');
            }
        } catch (error) {
            console.error('Failed to save group:', error);
            if (error.response) {
                const errorMessage = error.response.data?.error || '서버 오류가 발생했습니다.';
                alert(`저장 실패: ${errorMessage}`);
            } else if (error.request) {
                alert('서버와의 통신에 실패했습니다. 네트워크 상태를 확인해주세요.');
            } else {
                alert(`저장 실패: ${error.message}`);
            }
        }
    };

    const handleDelete = async () => {
        if (!group) return;

        if (!window.confirm('정말로 이 그룹을 삭제하시겠습니까?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await axios.delete(`/api/user-groups/${group.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                alert('그룹이 삭제되었습니다.');
                onClose();
                window.location.reload();
            } else {
                throw new Error(response.data.message || '그룹 삭제에 실패했습니다.');
            }
        } catch (error) {
            console.error('그룹 삭제 오류:', error);
            alert(error.message || '그룹 삭제에 실패했습니다.');
        }
    };

    // 2026-09-16 결정: 일반 관리자는 그룹 자체를 만들거나 고치거나 지울 수 없고,
    // 자기 그룹의 대상자만 넣고 뺀다. 서버도 같은 경계로 막는다
    // (src/server/adminScope/gate.js — user-groups 쓰기는 최고관리자 전용).
    const canEditGroup = isSuperAdminSession();
    const showInfoTab = canEditGroup && (modalTab === 'info' || !group?.id);

    return (
        <div className="sds-modal-overlay" onClick={onClose}>
            <div
                className="sds-modal sds-modal--wide"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="user-group-modal-title"
            >
                <div className="sds-modal__head">
                    <h2 id="user-group-modal-title" className="sds-modal__title">
                        {group?.id ? group.group_name : '사용자 그룹 생성'}
                    </h2>
                    <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={onClose} aria-label="닫기">
                        <X size={16} aria-hidden="true" />
                    </button>
                </div>

                {/* 새 그룹에는 구성원 탭이 없다 — 저장해서 id 가 생긴 뒤에 열린다(spec D2).
                    그룹을 고칠 수 없는 관리자에게는 구성원 하나뿐이라 탭 줄을 그리지 않는다. */}
                {group?.id && canEditGroup && (
                    <div className="sds-tabs user-groups-page__tabs" role="tablist">
                        <button
                            role="tab"
                            aria-selected={modalTab === 'info'}
                            className={`sds-tabs__item${modalTab === 'info' ? ' is-active' : ''}`}
                            onClick={() => setModalTab('info')}
                        >
                            그룹 정보
                        </button>
                        <button
                            role="tab"
                            aria-selected={modalTab === 'members'}
                            className={`sds-tabs__item${modalTab === 'members' ? ' is-active' : ''}`}
                            onClick={() => setModalTab('members')}
                        >
                            구성원
                        </button>
                    </div>
                )}

                <div className="sds-modal__body">
                    {!canEditGroup && !group?.id ? (
                        <div className="sds-notice">사용자 그룹 생성은 최고관리자만 할 수 있습니다.</div>
                    ) : showInfoTab ? (
                        <>
                            <div className="sds-field">
                                <label className="sds-label">그룹명<span className="sds-required">*</span></label>
                                <input
                                    name="group_name"
                                    className="sds-input"
                                    value={editedGroup.group_name}
                                    onChange={handleChange}
                                    placeholder="그룹명을 입력하세요"
                                />
                            </div>
                            <div className="sds-field">
                                <label className="sds-label">설명</label>
                                <textarea
                                    name="description"
                                    className="sds-textarea"
                                    value={editedGroup.description}
                                    onChange={handleChange}
                                    placeholder="그룹 설명을 입력하세요"
                                />
                            </div>
                            <div className="sds-field">
                                <label className="sds-label">그룹 역할</label>
                                <select
                                    name="group_role"
                                    className="sds-select"
                                    value={editedGroup.group_role}
                                    onChange={handleChange}
                                >
                                    {GROUP_ROLES.map((role) => (
                                        <option key={role.value} value={role.value}>
                                            {role.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="sds-field">
                                <label className="sds-label">상태</label>
                                <div className="sds-seg" role="radiogroup" aria-label="상태">
                                    {[[true, '활성'], [false, '비활성']].map(([value, label]) => {
                                        const active = editedGroup.available === value;
                                        return (
                                            <button
                                                key={label}
                                                role="radio"
                                                aria-checked={active}
                                                className={`sds-seg__item${active ? ' is-active' : ''}`}
                                                onClick={() => setEditedGroup(prev => ({ ...prev, available: value }))}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {selectedHospitalInfo && (
                                <div className="sds-field">
                                    <label className="sds-label">관리자 그룹</label>
                                    <select
                                        className="sds-select"
                                        value={selectedAdminGroup}
                                        onChange={(e) => setSelectedAdminGroup(e.target.value)}
                                    >
                                        <option value="">관리자 그룹 선택</option>
                                        {adminGroups.map(adminGroup => (
                                            <option key={adminGroup.id} value={String(adminGroup.id)}>
                                                {adminGroup.group_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* 생성 모드이고, 병원 선택이 필요할 때만 UI 표시 */}
                            {!group && showHospitalSelector && (
                                <>
                                    <div className="sds-field">
                                        <label className="sds-label">지역</label>
                                        <div className="user-groups-page__location">
                                            <select className="sds-select" value={selectedRegion} onChange={handleRegionChange}>
                                                <option value="">시/도 선택</option>
                                                {regions.map((region) => (
                                                    <option key={region.region_code} value={region.region_name}>
                                                        {region.region_name}
                                                    </option>
                                                ))}
                                            </select>
                                            <select
                                                className="sds-select"
                                                value={selectedDistrict}
                                                onChange={handleDistrictChange}
                                                disabled={!selectedRegion}
                                            >
                                                <option value="">전체 구/군</option>
                                                {districts.map((district) => (
                                                    <option key={district.district_code} value={district.district_name}>
                                                        {district.district_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="sds-field">
                                        <label className="sds-label">병원 검색</label>
                                        <div className="sds-toolbar">
                                            <input
                                                className="sds-input user-groups-page__search"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                placeholder="병원명을 입력하세요"
                                            />
                                            <button className="sds-btn sds-btn--secondary" onClick={handleHospitalSearch}>검색</button>
                                        </div>
                                        {searchResults.length > 0 && (
                                            <div className="sds-list user-groups-page__hospital-results">
                                                {searchResults.map((hospital) => (
                                                    <div
                                                        key={hospital.id}
                                                        className="sds-list__row sds-list__row--clickable"
                                                        onClick={() => handleHospitalSelect(hospital)}
                                                    >
                                                        <div className="sds-list__body">
                                                            <b>{hospital.institution_name}</b>
                                                            <small>{hospital.region_name} {hospital.district_name}</small>
                                                        </div>
                                                        <span className="sds-list__chev">›</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {selectedHospitalInfo && (
                                <div className="sds-field">
                                    <label className="sds-label">병원 정보</label>
                                    <dl className="ops-page__kv">
                                        <dt>병원명</dt><dd>{selectedHospitalInfo.institution_name}</dd>
                                        <dt>지역</dt><dd>{selectedHospitalInfo.region_name}</dd>
                                        <dt>구/군</dt><dd>{selectedHospitalInfo.district_name}</dd>
                                    </dl>
                                </div>
                            )}
                        </>
                    ) : (
                        <UserGroupMembers groupId={group.id} />
                    )}
                </div>

                <div className="sds-modal__foot">
                    {showInfoTab ? (
                        <>
                            {group && (
                                <button className="sds-btn sds-btn--danger" onClick={handleDelete}>삭제</button>
                            )}
                            <span className="sds-toolbar__spacer" />
                            <button className="sds-btn sds-btn--secondary" onClick={onClose}>취소</button>
                            <button className="sds-btn sds-btn--primary" onClick={handleSave}>{group ? '저장' : '생성'}</button>
                        </>
                    ) : (
                        <button className="sds-btn sds-btn--secondary" onClick={onClose}>닫기</button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default function UserGroups() {
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const groupsPerPage = 10;
    const [filterType, setFilterType] = useState('all');

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            const decodedToken = jwtDecode(token);
            const currentTime = Date.now() / 1000;

            if (decodedToken.exp < currentTime) {
                localStorage.removeItem('token');
                navigate('/login');
                return;
            }
        } catch (error) {
            localStorage.removeItem('token');
            navigate('/login');
            return;
        }

        fetchGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    useEffect(() => {
        const handleFilterChange = (event) => {
            setFilterType(event.detail.type);
        };
        window.addEventListener('filterChange', handleFilterChange);
        return () => window.removeEventListener('filterChange', handleFilterChange);
    }, []);

    const fetchGroups = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';
            const adminGroupIds = JSON.parse(localStorage.getItem('admin_group_ids')) || [];
            let params = {};

            if (isSuperAdmin) {
                // 슈퍼 관리자는 is_super_admin, filter_type, search_term 파라미터를 전송하여
                // 백엔드에서 필터링을 처리합니다.
                params = { is_super_admin: 'true', filter_type: filterType, search_term: searchTerm };
            } else {
                // 일반 관리자는 admin_group_ids를 사용하여 속한 병원의 모든 그룹을 조회
                params = {
                    admin_group_ids: adminGroupIds,
                    filter_type: filterType,
                    search_term: searchTerm
                };
            }

            const response = await axios.get(`${API_BASE_URL}/api/user-groups`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params
            });
            if (!response.data) {
                setGroups([]);
                setError('서버 응답 데이터가 없습니다.');
                return;
            }
            if (response.data.groups && Array.isArray(response.data.groups)) {
                const formattedGroups = response.data.groups.map(group => ({
                    id: group.id,
                    group_name: group.group_name || '',
                    description: group.description || '',
                    hospital_id: group.hospital_id,
                    hospital_name: group.hospital_name || '',
                    region_name: group.region_name || '',
                    district_name: group.district_name || '',
                    group_role: group.group_role || 'MEMBER',
                    available: Boolean(group.available),
                    region_code: group.region_code,
                    district_code: group.district_code,
                    postal_code: group.postal_code,
                    hospital_type_code: group.hospital_type_code,
                    admin_group_id: group.admin_group_id || null,
                    admin_group_name: group.admin_group_name || ''
                }));
                setGroups(formattedGroups);
                setCurrentPage(1);
            } else {
                setGroups([]);
                setError('그룹 데이터를 불러오는데 실패했습니다.');
            }
        } catch (error) {
            console.error('API 호출 실패:', error);

            let errorMessage = '데이터를 불러오는데 실패했습니다.';
            if (error.response) {
                if (error.response.status === 401) {
                    errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
                    localStorage.removeItem('token');
                    navigate('/login');
                } else {
                    errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                }
            } else if (error.request) {
                errorMessage = '서버에서 응답이 없습니다. 네트워크 연결을 확인해주세요.';
            }

            setError(errorMessage);
            setGroups([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterType]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSearch = () => {
        fetchGroups();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleGroupClick = (group) => {
        setSelectedGroup(group);
    };

    const handleCloseModal = () => {
        setSelectedGroup(null);
        setShowCreateModal(false);
    };

    const indexOfLastGroup = currentPage * groupsPerPage;
    const indexOfFirstGroup = indexOfLastGroup - groupsPerPage;
    const currentGroups = groups.slice(indexOfFirstGroup, indexOfLastGroup);
    const totalPages = Math.ceil(groups.length / groupsPerPage);

    return (
        <OpsLayout
            title="사용자 그룹 관리"
            description="기관별 사용자 그룹을 만들고 구성원을 정합니다."
            actions={isSuperAdminSession() ? (
                <button className="sds-btn sds-btn--primary" onClick={() => setShowCreateModal(true)}>
                    <Plus size={16} aria-hidden="true" />새 사용자 그룹 생성
                </button>
            ) : null}
        >
            <section className="ops-page__card user-groups-page">
                <div className="sds-toolbar">
                    <input
                        className="sds-input user-groups-page__search"
                        placeholder="그룹명으로 검색..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyPress={handleKeyPress}
                    />
                    <button className="sds-btn sds-btn--secondary" onClick={handleSearch}>검색</button>
                </div>

                {isLoading ? (
                    <div className="sds-notice">데이터를 불러오는 중입니다...</div>
                ) : error ? (
                    <div className="sds-notice sds-notice--error">{error}</div>
                ) : (
                    <>
                        <div className="sds-table-wrap">
                            <table className="sds-table">
                                <thead>
                                    <tr>
                                        <th>번호</th>
                                        <th>그룹명</th>
                                        <th>설명</th>
                                        <th>병원명</th>
                                        <th>지역</th>
                                        <th>구/군</th>
                                        <th>그룹 역할</th>
                                        <th>상태</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentGroups.length > 0 ? (
                                        currentGroups.map((group, index) => {
                                            const statusLabel = group.available ? '활성' : '비활성';
                                            return (
                                                <tr
                                                    key={group.id}
                                                    onClick={() => handleGroupClick(group)}
                                                    className="ops-page__row--clickable"
                                                >
                                                    <td>{indexOfFirstGroup + index + 1}</td>
                                                    <td><b>{group.group_name}</b></td>
                                                    <td>{group.description}</td>
                                                    <td>{group.hospital_name}</td>
                                                    <td>{group.region_name}</td>
                                                    <td>{group.district_name}</td>
                                                    <td>{getRoleLabel(group.group_role)}</td>
                                                    <td>
                                                        <span className={`sds-badge sds-badge--${opsStatusTone(statusLabel)}`}>{statusLabel}</span>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="sds-empty">데이터가 없습니다.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="sds-pagination">
                            <button
                                className="sds-pagination__btn"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                aria-label="이전 페이지"
                            >
                                <ChevronLeft size={16} aria-hidden="true" />
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    className={`sds-pagination__btn${currentPage === page ? ' is-active' : ''}`}
                                    aria-current={currentPage === page ? 'page' : undefined}
                                    onClick={() => setCurrentPage(page)}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                className="sds-pagination__btn"
                                onClick={() => setCurrentPage(prev => Math.max(1, Math.min(prev + 1, totalPages)))}
                                disabled={currentPage >= totalPages}
                                aria-label="다음 페이지"
                            >
                                <ChevronRight size={16} aria-hidden="true" />
                            </button>
                        </div>
                    </>
                )}
            </section>

            {(selectedGroup || showCreateModal) && (
                <GroupModal
                    group={selectedGroup}
                    onClose={handleCloseModal}
                />
            )}
        </OpsLayout>
    );
}
